<template>
	<div ref="wrapper" class="world-wrapper">
		<canvas ref="world-canvas"></canvas>
		<div v-if="tags.length">
			<ja-building-tag
				v-for="(tag, index) in tags"
				:key="index"
				:disabled="disabled"
				:category="tag.category"
				:coords-y="tag.y"
				:coords-x="tag.x"
				@click.native="selectCategory(tag.category)"
			></ja-building-tag>
		</div>
	</div>
</template>
<script>
import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import {
	buildings,
	localSponsor as localSponsorConfig,
	secondarySponsor as secondarySponsorConfig
} from '../../configs/world-config';
import BuildingTag from './BuildingTag';
import CategoryCheck from '@/mixins/CategoryCheck';

export default {
	components: {
		jaBuildingTag: BuildingTag
	},
	mixins: [CategoryCheck],
	props: {
		zoom: {
			type: Number,
			default: 1
		},
		categories: {
			type: Array,
			default: () => {
				return [];
			}
		},
		resources: {
			type: Object,
			default: () => {
				return {};
			}
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			pixiApp: null,
			viewport: null,
			mapContainer: null,
			resolutionX: 6500,
			resolutionY: 5000,
			tags: [],
			clickDisabled: false
		};
	},
	computed: {
		localSponsor() {
			return this.$store.getters.localSponsor('PrimaryBillboard');
		},
		secondarySponsor() {
			return this.$store.getters.localSponsor('SecondaryBillboard');
		}
	},
	watch: {
		localSponsor(value) {
			if (value) {
				this.setBiggerSponsorImages(value, 'local-sponsor');
			}
		},
		secondarySponsor(value) {
			if (value) {
				this.setBiggerSponsorImages(value, 'secondary-sponsor');
			}
		},
		zoom(value) {
			this.viewport.setZoom(value, true);
		},
		categories(newCategories) {
			this.setTags();
			newCategories.forEach(category => {
				let building = this.mapContainer.getChildByName(category.assetName);
				this.makeBuildingInteractive(building, category);
			});
		},
		disabled(value) {
			if (!value) {
				this.categories.forEach(category => {
					if (this.isCategoryEnabled(category)) {
						let building = this.mapContainer.getChildByName(category.assetName);
						this.makeBuildingInteractive(building, category);
					}
				});
			}
		}
	},
	mounted() {
		PIXI.utils.skipHello();
		// PIXI.utils.clearTextureCache();
		// PIXI.utils.destroyTextureCache();
		this.pixiApp = new PIXI.Application({
			view: this.$refs['world-canvas'],
			transparent: true,
			autoResize: true,
			resizeTo: this.$refs['wrapper'],
			resolution: 1 || window.devicePixelRatio
		});

		this.viewport = new Viewport({
			screenWidth: this.$refs['world-canvas'].clientWidth,
			screenHeight: this.$refs['world-canvas'].clientHeight,
			worldWidth: this.resolutionX,
			worldHeight: this.resolutionY,
			interaction: this.pixiApp.renderer.plugins.interaction
		});

		this.pixiApp.stage.addChild(this.viewport);

		this.viewport.drag({ wheel: false, underflow: 'top' });
		this.viewport.decelerate();
		this.viewport.clamp({
			left: true,
			right: true,
			top: true,
			bottom: true,
			direction: 'all'
		});

		this.loadMap();
		this.loadBuildings();
		this.loadBiggerBillboard();
		if (this.localSponsor || this.secondarySponsor) {
			this.setBiggerSponsorImages();
		}
		this.viewport.fit();

		this.viewport.on('moved', () => {
			this.recalculateTags();
		});

		this.viewport.on('drag-start', () => {
			this.clickDisabled = true;
			this.recalculateTags();
		});

		this.viewport.on('drag-end', () => {
			this.recalculateTags();
			setTimeout(() => {
				this.clickDisabled = false;
			}, 200);
		});

		this.viewport.on('zoomed', () => {
			this.recalculateTags();
		});

		this.viewport.on('zoomed-end', () => {
			this.recalculateTags();
		});

		window.addEventListener('resize', this.resizeViewport);

		this.viewport.setZoom(this.zoom, true);

		this.pixiApp.render();
		this.$emit('worldRendered');
		this.setTags();
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.resizeViewport);
		this.viewport.destroy();
		this.pixiApp.destroy();
	},
	methods: {
		resizeViewport() {
			this.viewport.screenWidth = this.$refs['world-canvas'].clientWidth;
			this.viewport.screenHeight = this.$refs['world-canvas'].clientHeight;
		},
		setTags() {
			const tags = [];
			this.categories.forEach(category => {
				let sprite = this.mapContainer.getChildByName(category.assetName);
				if (sprite) {
					let spritePosition = sprite.getGlobalPosition();
					let tag = {
						category: category,
						x: this.calculateTagX(spritePosition.x, sprite.width),
						y: spritePosition.y
					};

					tags.push(tag);
				}
			});

			this.tags = tags;
		},
		recalculateTags() {
			this.tags.forEach(tag => {
				let sprite = this.mapContainer.getChildByName(tag.category.assetName);
				let spritePosition = sprite.getGlobalPosition();

				tag.x = this.calculateTagX(spritePosition.x, sprite.width);
				tag.y = spritePosition.y;
			});
		},
		calculateTagX(position, spriteWidth) {
			return position + (spriteWidth * this.zoom) / 2;
		},
		loadMap() {
			this.mapContainer = new PIXI.Container();
			const worldTopLeft = PIXI.Sprite.from(
				this.resources['world-top-left'].texture
			);

			worldTopLeft.position.x = 0;
			worldTopLeft.position.y = 0;

			this.mapContainer.addChild(worldTopLeft);

			const worldTopRight = PIXI.Sprite.from(
				this.resources['world-top-right'].texture
			);

			worldTopRight.position.x = this.resolutionX / 2;
			worldTopRight.position.y = 0;
			this.mapContainer.addChild(worldTopRight);

			const worldBottomRight = PIXI.Sprite.from(
				this.resources['world-bottom-left'].texture
			);

			worldBottomRight.position.x = 0;
			worldBottomRight.position.y = this.resolutionY / 2;

			this.mapContainer.addChild(worldBottomRight);

			const worldBottomLeft = PIXI.Sprite.from(
				this.resources['world-bottom-right'].texture
			);

			worldBottomLeft.position.x = this.resolutionX / 2;
			worldBottomLeft.position.y = this.resolutionY / 2;

			this.mapContainer.addChild(worldBottomLeft);
			this.mapContainer.interactive = true;

			this.viewport.addChild(this.mapContainer);
		},

		loadBuildings() {
			this.categories.forEach(category => {
				if (this.resources[category.assetName]) {
					let building = PIXI.Sprite.from(
						this.resources[category.assetName].texture
					);
					let buildingConfig = buildings[category.assetName];
					if (buildingConfig) {
						building.name = category.assetName;
						building.position.x = buildingConfig.x;
						building.position.y = buildingConfig.y;

						this.mapContainer.addChild(building);
						this.makeBuildingInteractive(building, category);

						if (buildingConfig.logoSponsorX > 0) {
							const logoTexture = this.resources.hasOwnProperty(
								`billboard_${category.assetName}`
							)
								? this.resources[`billboard_${category.assetName}`].texture
								: this.resources['small-default-billboard'].texture;

							let logo = PIXI.Sprite.from(logoTexture);

							const billboardTexture = this.resources['small-billboard']
								.texture;

							const billboard = PIXI.Sprite.from(billboardTexture);
							billboard.position.x = buildingConfig.logoSponsorX + 1;
							billboard.position.y = buildingConfig.logoSponsorY - 4;
							billboard.width = 230;
							billboard.height = 84;

							logo.width = buildings.sponsorLogo.width;
							logo.height = buildings.sponsorLogo.height;
							logo.position.x = 12;
							logo.position.y = 13;

							billboard.addChild(logo);
							this.mapContainer.addChild(billboard);
						}
					}
				}
			});
		},

		loadBiggerBillboard() {
			let billboard = PIXI.Sprite.from(
				this.resources['local-sponsor-billboard'].texture
			);

			let secondaryBillboard = PIXI.Sprite.from(
				this.resources['local-sponsor-billboard'].texture
			);

			// In the center of the map
			billboard.name = 'local-sponsor-billboard';
			billboard.position.x = localSponsorConfig.x;
			billboard.position.y = localSponsorConfig.y;

			this.mapContainer.addChild(billboard);

			// Near the sea
			secondaryBillboard.name = 'secondary-sponsor-billboard';
			secondaryBillboard.position.x = secondarySponsorConfig.x;
			secondaryBillboard.position.y = secondarySponsorConfig.y;

			this.mapContainer.addChild(secondaryBillboard);
		},

		setBiggerSponsorImages(image, billboard) {
			let loader = PIXI.Loader.shared;

			const initalLoad = !image && !billboard;

			const localSponsorResource = loader.resources['local-sponsor'];
			const secondarySponsorResource = loader.resources['secondary-sponsor'];

			if (!localSponsorResource && initalLoad) {
				loader.add('local-sponsor', this.localSponsor);
			}

			if (!secondarySponsorResource && initalLoad) {
				loader.add('secondary-sponsor', this.secondarySponsor);
			}

			const biggerBillboardsConfig = {
				'local-sponsor': localSponsorConfig,
				'secondary-sponsor': secondarySponsorConfig
			};

			if (!initalLoad && (!localSponsorResource || secondarySponsorResource)) {
				loader.add(billboard, image);
			}

			loader.load((_, resources) => {
				Object.keys(resources).forEach(resourceName => {
					if (resources[resourceName].name === resources[resourceName].url) {
						return;
					}

					let logo = PIXI.Sprite.from(resources[resourceName].texture);

					logo.width = biggerBillboardsConfig[resourceName].logoWidth;
					logo.height = biggerBillboardsConfig[resourceName].logoHeight;

					logo.position.x = biggerBillboardsConfig[resourceName].logoX;
					logo.position.y = biggerBillboardsConfig[resourceName].logoY;

					this.mapContainer.addChild(logo);
				});
			});
		},
		enableBuilding(building) {
			building.interactive = true;
			building.buttonMode = true;
			building.defaultCursor = 'pointer';
		},

		disableBuilding(building) {
			building.interactive = false;
			building.buttonMode = false;
			building.defaultCursor = 'default';
		},

		makeBuildingInteractive(building, category) {
			if (building) {
				building.off('click');
				building.off('tap');
				if (this.isCategoryDisabled(category)) {
					this.disableBuilding(building);
				} else {
					this.enableBuilding(building);
					building.on('click', () => {
						if (!this.clickDisabled) {
							this.selectCategory(category);
						}
					});

					building.on('tap', () => {
						if (!this.clickDisabled) {
							this.selectCategory(category);
						}
					});
				}
			}
		},

		selectCategory(category) {
			if (this.isCategoryEnabled(category)) {
				this.$emit('categorySelected', category);
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.world-wrapper {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
	cursor: move;
}
</style>
